import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { validateBranchTargets } from '@marketing-hub/shared';
import { apiFetch, ApiError } from '@/api/client';
import type { EmailTemplate } from '@/api/types';
import { Button } from '@/components/ui/Button';
import { Field, TextInput } from '@/components/ui/Field';
import { LoadingState } from '@/components/StateMessage/StateMessage';
import { SequenceStepCard, type EditableSequenceStep } from './SequenceStepCard';
import { SequenceSimulatePanel } from './SequenceSimulatePanel';
import styles from './sequenceEditor.module.css';

function normalizeStepOrders(steps: EditableSequenceStep[]): EditableSequenceStep[] {
  return steps
    .slice()
    .sort((a, b) => a.step_order - b.step_order)
    .map((s, i) => ({ ...s, step_order: i }));
}

function emptyStep(order: number): EditableSequenceStep {
  return {
    step_order: order,
    wait_days: order === 0 ? 0 : 3,
    subject_template: '',
    body_text_template: '',
    email_template_id: null,
    branch_rules: [],
    step_channel: 'email',
  };
}

type Props = {
  projectId: string;
  sequenceId: string;
  templates: EmailTemplate[];
};

export function SequenceTimelineEditor({ projectId, sequenceId, templates }: Props) {
  const qc = useQueryClient();
  const [tab, setTab] = useState<'steps' | 'simulate'>('steps');
  const [steps, setSteps] = useState<EditableSequenceStep[]>([]);
  const [name, setName] = useState('');

  const detailQ = useQuery({
    queryKey: ['sequence-detail', sequenceId],
    queryFn: () =>
      apiFetch<{
        sequence: { id: string; name: string };
        steps: EditableSequenceStep[];
      }>(`/api/sequences/${sequenceId}`),
  });

  const lockQ = useQuery({
    queryKey: ['sequence-running-lock', sequenceId, projectId],
    queryFn: async () => {
      const { campaigns } = await apiFetch<{ campaigns: { id: string; status: string; sequence_id: string }[] }>(
        `/api/campaigns?project_id=${projectId}`,
      );
      return campaigns.some((c) => c.status === 'running' && c.sequence_id === sequenceId);
    },
  });

  useEffect(() => {
    if (!detailQ.data) return;
    setName(detailQ.data.sequence.name);
    setSteps(
      detailQ.data.steps.map((s) => ({
        ...s,
        email_template_id: s.email_template_id ?? null,
        branch_rules: s.branch_rules ?? [],
        step_channel: s.step_channel === 'voice' ? 'voice' : 'email',
      })),
    );
  }, [detailQ.data]);

  const branchIssues = useMemo(
    () =>
      validateBranchTargets(
        steps.map((s) => ({ step_order: s.step_order, branch_rules: s.branch_rules })),
      ),
    [steps],
  );

  const saveM = useMutation({
    mutationFn: async () => {
      const normalized = normalizeStepOrders(steps);
      await apiFetch(`/api/sequences/${sequenceId}/steps`, {
        method: 'PUT',
        body: JSON.stringify({ steps: normalized }),
      });
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['sequence-detail', sequenceId] });
      void qc.invalidateQueries({ queryKey: ['sequences', projectId] });
    },
  });

  const readOnly = lockQ.data === true;

  function updateStep(index: number, next: EditableSequenceStep) {
    setSteps((prev) => prev.map((s, i) => (i === index ? next : s)));
  }

  function moveStep(index: number, delta: -1 | 1) {
    setSteps((prev) => {
      const sorted = normalizeStepOrders(prev);
      const target = index + delta;
      if (target < 0 || target >= sorted.length) return prev;
      const copy = [...sorted];
      const [item] = copy.splice(index, 1);
      copy.splice(target, 0, item!);
      return normalizeStepOrders(copy);
    });
  }

  function duplicateStep(index: number) {
    setSteps((prev) => {
      const sorted = normalizeStepOrders(prev);
      if (sorted.length >= 20) return prev;
      const src = sorted[index]!;
      const copy = { ...src, branch_rules: src.branch_rules.map((r) => ({ ...r })) };
      const next = [...sorted.slice(0, index + 1), copy, ...sorted.slice(index + 1)];
      return normalizeStepOrders(next);
    });
  }

  function deleteStep(index: number) {
    if (!window.confirm('Stap verwijderen?')) return;
    setSteps((prev) => normalizeStepOrders(prev.filter((_, i) => i !== index)));
  }

  function addStep() {
    setSteps((prev) => {
      const sorted = normalizeStepOrders(prev);
      if (sorted.length >= 20) return prev;
      return [...sorted, emptyStep(sorted.length)];
    });
  }

  if (detailQ.isLoading) return <LoadingState label="Sequence laden…" />;

  const sortedSteps = normalizeStepOrders(steps);

  return (
    <div className={styles.editorPanel}>
      {readOnly ? (
        <p className={styles.lockBanner}>
          Deze sequence hoort bij een running campagne — alleen-lezen tot campagne stopt.
        </p>
      ) : null}
      <div className={styles.editorHead}>
        <Field label="Naam">
          <TextInput value={name} disabled aria-readonly />
        </Field>
        <div>
          <Button type="button" disabled={readOnly || saveM.isPending || branchIssues.length > 0} onClick={() => saveM.mutate()}>
            {saveM.isPending ? 'Opslaan…' : 'Stappen opslaan'}
          </Button>
        </div>
      </div>
      {branchIssues.length ? (
        <p className={styles.saveError} style={{ padding: '0 var(--space-lg)' }}>
          Los branch-fouten op vóór opslaan ({branchIssues.length}).
        </p>
      ) : null}
      {saveM.isError && saveM.error instanceof ApiError ? (
        <p className={styles.saveError} style={{ padding: '0 var(--space-lg)' }}>
          {saveM.error.message}
        </p>
      ) : null}

      <div className={styles.tabRow}>
        <button type="button" className={tab === 'steps' ? styles.tabActive : styles.tab} onClick={() => setTab('steps')}>
          Timeline
        </button>
        <button type="button" className={tab === 'simulate' ? styles.tabActive : styles.tab} onClick={() => setTab('simulate')}>
          Simulate
        </button>
      </div>

      <div className={styles.editorBody}>
        {tab === 'steps' ? (
          <>
            <ul className={styles.timeline}>
              {sortedSteps.map((step, index) => (
                <SequenceStepCard
                  key={`${step.step_order}-${index}`}
                  step={step}
                  steps={sortedSteps}
                  templates={templates}
                  disabled={readOnly}
                  canMoveUp={index > 0}
                  canMoveDown={index < sortedSteps.length - 1}
                  onChange={(next) => updateStep(index, next)}
                  onMove={(d) => moveStep(index, d)}
                  onDuplicate={() => duplicateStep(index)}
                  onDelete={() => deleteStep(index)}
                />
              ))}
            </ul>
            <Button type="button" variant="secondary" disabled={readOnly || sortedSteps.length >= 20} onClick={addStep}>
              + Stap
            </Button>
          </>
        ) : (
          <SequenceSimulatePanel projectId={projectId} sequenceId={sequenceId} />
        )}
      </div>
    </div>
  );
}
